var _react_refresh_temp_1;
var _react_refresh_temp_2;
_react_refresh_temp_2 = $RefreshSig$();
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { Box, FieldValidation, TagInput, } from '#design-system';
import { useField, useFieldDependency } from '#form-builder';
import { composeValidators } from '#form-builder/services';
const defaultValidationRules = [];
export const TagInputField = ({ 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, autoComplete = 'off', autoFocus, className, clientErrorMessage, 'data-testid': testid, description, disabled, generalServerErrorCode, getTagErrorText, id, initialValue, label, labelTooltip, placeholder, required, rules = defaultValidationRules, shouldDisablePasswordManagers = true, tagListClassName, tooltipPosition, ...props }) => {
    _react_refresh_temp_2();
    const builtInitialValue = useMemo(() => {
        if (typeof initialValue === 'function') {
            return initialValue({});
        }
        else {
            return initialValue ?? [];
        }
    }, [initialValue]);
    // This is an object which holds the errors from the backend
    // [k in value: string ]: ValidationErrors associated with this value
    // It associates an entry from the `value` array of strings to its server error(s)
    // It's used to compose the custom runtime validator which marks the tag of the value as having an error
    // It's updated only when new server errors arrive from the backend when the form is re-submitted.
    const [serverErrorMap, setServerErrorMap] = useState({});
    // Create a composed validator. This will take the validator passed as a prop to the field
    // and combine it with a new validator, created using the received server errors.
    const clientAndServerValidators = useMemo(() => {
        const validatorFactory = () => {
            // The 'runtime' validator takes a snapshot of the value array at the moment server errors are received
            // and check the serverErrorMap for any keys that are still in the value array.
            const serverRuntimeValidator = (value) => {
                const result = value
                    .map((entry, idx) => {
                    const result = serverErrorMap[entry];
                    return result
                        ? result.map((error) => ({
                            ...error,
                            isServerError: false,
                            path: [idx],
                        }))
                        : undefined;
                })
                    .filter(Boolean)
                    .flat();
                return {
                    isValid: !result.length,
                    errors: result,
                };
            };
            // If validator is passed as a prop, compose it with our runtime one
            if (props.validator) {
                return composeValidators(props.validator, serverRuntimeValidator);
            }
            else {
                return serverRuntimeValidator;
            }
        };
        return validatorFactory();
    }, [props.validator, serverErrorMap]);
    const { 
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- dependencyValue is any on purpose
    dependencyValue, errors, fieldRef, isFocused, isTouched, onBlurHandler, onChangeHandler, onFocusHandler, triggerValidation, value, } = useField({
        autoFocus,
        ...props,
        disabled,
        initialValue: builtInitialValue,
        // Pass the new validator. This will be either the validator passed as props, or a composed validator
        // which also checks for values that had server errors associated with them.
        validator: clientAndServerValidators,
    });
    const dependantData = useFieldDependency({
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- dependencyValue is any on purpose
        dependencyValue,
        disabled,
        label,
        required,
    });
    const serverErrors = errors.filter((error) => error.isServerError);
    useEffect(() => {
        // We read the server errors once and then
        // they are set as non-server ones.
        // So if we don't check for the length
        // we will override the server error map immediately.
        if (serverErrors.length) {
            const remappedErrors = serverErrors.reduce((accum, current) => {
                const currentValue = value[current['path'][current['path'].length - 1]];
                if (currentValue) {
                    accum[currentValue] = [
                        { ...current, isServerError: false },
                    ];
                }
                return accum;
            }, {});
            setServerErrorMap(remappedErrors);
        }
    }, [serverErrors, value]);
    // Force the validators to run again when server errors are received
    // Needed, as validators usually run only when onChangeHandler
    // is called for the field
    useLayoutEffect(() => {
        if (serverErrors.length) {
            triggerValidation();
        }
    }, [serverErrors.length, triggerValidation]);
    const remappedErrors = useMemo(() => {
        return errors.reduce((accum, current) => {
            const currentValue = value[current['path'][current['path'].length - 1]];
            if (currentValue) {
                if (accum[currentValue]) {
                    accum[currentValue] = [
                        ...accum[currentValue],
                        { ...current, isServerError: false },
                    ];
                }
                else {
                    accum[currentValue] = [
                        { ...current, isServerError: false },
                    ];
                }
            }
            return accum;
        }, {});
    }, [errors, value]);
    const fieldValidationErrors = useMemo(() => {
        let hasServerError = false;
        const remappedValidationErrors = errors.map((error) => {
            if (error.isServerError === false) {
                hasServerError = true;
            }
            return {
                ...error,
                // We check if we've explicitly set isServerError to false. This is done in the useEffect above
                // setting the state of the server errors. Because errors coming from the BE have isServerError true when received,
                // we set it explicitly to FALSE, and add them to our "custom validator function". This way, they show up as validation errors
                // FE Validation errors will not have this property at all as it is optional, so this check will ignore them.
                isServerError: error.isServerError === false,
            };
        });
        if (hasServerError) {
            return [
                ...remappedValidationErrors.filter((error) => !error.isServerError),
                {
                    path: [],
                    code: generalServerErrorCode || '',
                    message: '',
                    isServerError: true,
                },
            ];
        }
        else {
            return remappedValidationErrors;
        }
    }, [errors, generalServerErrorCode]);
    const hasErrors = Boolean(fieldValidationErrors.length) && !isFocused && isTouched;
    const fieldValidationId = `field-validation-${id}`;
    return (_jsxs(Box, { className: className, gap: "xs", isFullWidth: true, orientation: "vertical", children: [_jsx(TagInput, { "aria-describedby": fieldValidationId, ...(fieldValidationErrors.length && {
                    'aria-errormessage': fieldValidationId,
                }), ...(fieldValidationErrors.length && { 'aria-invalid': true }), "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy, autoComplete: autoComplete, "data-testid": testid, description: description, disabled: dependantData.disabled, getTagErrorText: getTagErrorText, hasErrors: hasErrors, id: id, label: dependantData.label, labelTooltip: labelTooltip, onBlur: onBlurHandler, onChange: onChangeHandler, onFocus: onFocusHandler, placeholder: placeholder, ref: fieldRef, required: dependantData.required, shouldDisablePasswordManagers: shouldDisablePasswordManagers, tagListClassName: tagListClassName, tooltipPosition: tooltipPosition, validatorErrors: remappedErrors, value: value }), _jsx(FieldValidation, { clientErrorMessage: clientErrorMessage, "data-testid": testid, errors: fieldValidationErrors, id: fieldValidationId, isActive: isTouched, isFocused: isFocused, rules: rules })] }));
};
_react_refresh_temp_1 = TagInputField;
$RefreshReg$(_react_refresh_temp_1, "TagInputField");
_react_refresh_temp_2(TagInputField, "MDNdlR98+T5HMVtKfYHMduphSk4=", false, () => [useField, useFieldDependency]);
